// The audit result contract, mirrored from redraw's pydantic models
// (`pipeline/source_preparation/types.py`). Those are FrozenStrictModel: extra
// keys are rejected and the finding fields have no defaults, so every key must
// be present with an explicit null where empty.
import { resolve, type Doc } from "@lingjingai/screenplay";
import { z } from "zod";

export const CATEGORIES = [
  "scene-reference", "actor-identity", "wardrobe", "asset",
  "dialogue-action", "scene-boundary", "timeline", "other",
] as const;
export const OUTCOMES = ["fixed", "unchanged", "unresolved"] as const;
export const CONFIDENCES = ["high", "medium", "low"] as const;
export const SUBJECT_KINDS = ["actor", "state", "location", "prop", "action", "scene"] as const;
export const EVIDENCE_KINDS = ["scriptctl", "video", "gemini", "frame", "structure"] as const;

export interface AuditAnchor {
  episode_number: number;
  episode_id: string | null;
  scene_number: number | null;
  scene_id: string | null;
  action_id: string | null;
  timestamp: string | null;
  end_timestamp: string | null;
}
export interface AuditSubject {
  kind: (typeof SUBJECT_KINDS)[number];
  id: string | null;
  name: string;
}
export interface AuditEvidence {
  kind: (typeof EVIDENCE_KINDS)[number];
  label: string;
  detail: string;
  timestamp: string | null;
  end_timestamp: string | null;
  evidence_id: string | null;
}
export interface AuditFinding {
  id: string;
  category: (typeof CATEGORIES)[number];
  title: string;
  issue: string;
  impact: string;
  resolution: string;
  outcome: (typeof OUTCOMES)[number];
  confidence: (typeof CONFIDENCES)[number];
  anchor: AuditAnchor;
  subject: AuditSubject | null;
  before: string | null;
  after: string | null;
  unresolved_reason: string | null;
  evidence: AuditEvidence[];
}
export interface AuditAgentResult {
  episode_number: number;
  message: string;
  findings: AuditFinding[];
}

/**
 * What the model is actually asked for. Deliberately flatter than AuditFinding:
 * `at` is one address string and the anchor is derived from it, because a
 * hand-written 7-field anchor can contradict itself. `evidence` is a list of
 * `kind|label|detail` strings for the same reason.
 */
export const FINDING_INPUT = z.object({
  category: z.enum(CATEGORIES),
  title: z.string().min(1).describe("一句话标题"),
  issue: z.string().min(1).describe("问题是什么"),
  impact: z.string().min(1).describe("不修会影响什么"),
  resolution: z.string().min(1).describe("做了什么，或为什么不做"),
  outcome: z.enum(OUTCOMES),
  confidence: z.enum(CONFIDENCES),
  at: z.string().min(1)
    .describe("定位地址：集 `3`、场 `3-2`、单条 `3-2-5`、正式 ID、或 `<资产>/<状态>`"),
  subject: z.string().optional().describe("涉及的资产名或 ID，例如 `向北` 或 `actQYEeTr/default`"),
  before: z.string().optional(),
  after: z.string().optional(),
  unresolved_reason: z.string().optional().describe("outcome=unresolved 时必填"),
  evidence: z.array(z.string()).optional()
    .describe("每条写成 `kind|label|detail`，kind ∈ scriptctl|video|gemini|frame|structure"),
});
export type FindingInput = z.infer<typeof FINDING_INPUT>;

const EMPTY_ANCHOR = (episode: number): AuditAnchor => ({
  episode_number: episode,
  episode_id: null, scene_number: null, scene_id: null,
  action_id: null, timestamp: null, end_timestamp: null,
});

/**
 * Expand one address into the full anchor. Resolution failures are not fatal —
 * a finding about something that does not exist any more (a deleted duplicate
 * actor, say) still has to be reportable — so the episode number always stands
 * and the rest degrades to null.
 */
export function anchorOf(doc: Doc, at: string, episode: number): AuditAnchor {
  const anchor = EMPTY_ANCHOR(episode);
  let node;
  try { node = resolve(doc, at); } catch { return anchor; }
  if (node.t === "ep") {
    return { ...anchor, episode_number: node.ep.no, episode_id: node.ep.id };
  }
  if (node.t === "scene") {
    const first = node.scene.actions.find((a: { ts?: string }) => a.ts);
    const last = [...node.scene.actions].reverse().find((a: { ts?: string }) => a.ts);
    return {
      ...anchor,
      episode_number: node.scene.ep.no, episode_id: node.scene.ep.id,
      scene_number: node.scene.no, scene_id: node.scene.id,
      timestamp: first?.ts ?? null, end_timestamp: last?.ts ?? null,
    };
  }
  if (node.t === "action") {
    const a = node.action;
    return {
      ...anchor,
      episode_number: a.scene.ep.no, episode_id: a.scene.ep.id,
      scene_number: a.scene.no, scene_id: a.scene.id,
      action_id: doc.version === 4 ? a.anchor : null,
      timestamp: a.ts ?? null, end_timestamp: null,
    };
  }
  return anchor;
}

function subjectOf(doc: Doc, raw: string | undefined): AuditSubject | null {
  if (!raw?.trim()) return null;
  try {
    const node = resolve(doc, raw);
    if (node.t === "asset") return { kind: node.asset.kind, id: node.asset.id, name: node.asset.name };
    if (node.t === "state") {
      return { kind: "state", id: `${node.asset.id}/${node.state.id}`, name: node.state.name };
    }
    if (node.t === "action") return { kind: "action", id: node.action.anchor, name: raw };
    if (node.t === "scene") return { kind: "scene", id: node.scene.id, name: raw };
  } catch { /* fall through to a name-only subject */ }
  return { kind: "actor", id: null, name: raw };
}

function evidenceOf(lines: string[] | undefined): AuditEvidence[] {
  return (lines ?? []).map((line) => {
    const [rawKind, label, ...rest] = line.split("|");
    const kind = (EVIDENCE_KINDS as readonly string[]).includes(rawKind?.trim())
      ? (rawKind.trim() as AuditEvidence["kind"]) : "structure";
    return {
      kind,
      label: (label ?? rawKind ?? "").trim() || "evidence",
      detail: rest.join("|").trim() || (label ?? "").trim(),
      timestamp: null, end_timestamp: null, evidence_id: null,
    };
  });
}

/** Build the wire-format finding redraw will validate. */
export function toFinding(
  doc: Doc, input: FindingInput, episode: number, seq: number,
): AuditFinding {
  return {
    id: `episode-${episode}-${String(seq).padStart(3, "0")}-${input.category}`,
    category: input.category,
    title: input.title,
    issue: input.issue,
    impact: input.impact,
    resolution: input.resolution,
    outcome: input.outcome,
    confidence: input.confidence,
    anchor: anchorOf(doc, input.at, episode),
    subject: subjectOf(doc, input.subject),
    before: input.before ?? null,
    after: input.after ?? null,
    unresolved_reason: input.unresolved_reason ?? null,
    evidence: evidenceOf(input.evidence),
  };
}
